<script lang="ts" setup>
import { QuestionFilled } from "@element-plus/icons-vue"
import { storeToRefs } from "pinia"
import { computed, nextTick, onMounted, ref } from "vue"
import { useRoute } from "vue-router"
import VersionHistoryDialog from "@/components/dialog/VersionHistoryDialog.vue"
import { useAppStore } from "@/store/modules/app"
import { usePermissionStore } from "@/store/modules/permission"
import { resolveDisplayedSystemVersion } from "@/utils/businessContext/versionContext"
// import { useSettingsStore } from "@/store/modules/settings"
import SidebarItem from "./SidebarItem.vue"
import { flattenSidebarRoutes } from "./sidebarRoutes"

const route = useRoute()
const appStore = useAppStore()
const permissionStore = usePermissionStore()

// const settingsStore = useSettingsStore()

// const { showSidebarLogo } = storeToRefs(settingsStore)
const { currentFrontVersion } = storeToRefs(appStore)

const activeMenu = computed(() => {
  const { meta, path } = route
  if (meta?.activeMenu) {
    return meta.activeMenu
  }
  return path
})

const isCollapse = computed(() => {
  // return !appStore.sidebar.opened
  return false
})

const sidebarRoutes = computed(() => flattenSidebarRoutes(permissionStore.routes))

// 版本历史对话框显示状态
const showVersionHistory = ref(false)

// 当前系统版本号
const currentVersion = computed(() => {
  const displayedSystemVersion = resolveDisplayedSystemVersion(appStore.currentSystemVersion)
  if (displayedSystemVersion) {
    return `${displayedSystemVersion}(${currentFrontVersion.value})`
  } else {
    return 'fe-' + currentFrontVersion.value
  }
})

// 版本信息区域的高度
const versionInfoHeight = ref(44)

// 组件挂载后获取版本信息区域的实际高度
onMounted(() => {
  nextTick(() => {
    const versionInfoEl = document.querySelector('.version-info')
    if (versionInfoEl) {
      versionInfoHeight.value = versionInfoEl.getBoundingClientRect().height
    }
  })
})
</script>

<template>
  <div class="has-logo">
    <!-- <SidebarLogo v-if="showSidebarLogo" :collapse="isCollapse" /> -->
    <el-scrollbar wrap-class="scrollbar-wrapper" :style="{ paddingBottom: `${versionInfoHeight}px` }">
      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapse"
        background-color="transparent"
        text-color="var(--bussiness-sidebar-text-color)"
        active-text-color="var(--bussiness-sidebar-active-text-color)"
        :unique-opened="true"
        :collapse-transition="false"
        mode="vertical"
      >
        <SidebarItem
          v-for="sidebarRoute in sidebarRoutes"
          :key="sidebarRoute.path"
          :item="sidebarRoute"
          :base-path="sidebarRoute.path"
          :is-collapse="isCollapse"
        />
      </el-menu>
    </el-scrollbar>

    <!-- 版本信息区域 -->
    <div class="version-info" :class="{ collapsed: isCollapse }">
      <span class="version-number">{{ currentVersion }}</span>
      <el-icon
        class="version-history-icon"
        @click="showVersionHistory = true"
        title="查看更新历史"
      >
        <QuestionFilled />
      </el-icon>
    </div>

    <!-- 版本历史对话框 -->
    <VersionHistoryDialog
      v-model="showVersionHistory"
    />
  </div>
</template>

<style lang="scss" scoped>
@mixin tip-line {
  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 100%;
    content: '';
    background-color: var(--bussiness-sidebar-tip-line-color);
    // background-color: var(--v3-sidebar-menu-tip-line-bg-color);
  }
}

:host,
.has-logo {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: transparent;
}

.el-scrollbar {
  flex: 1 1 0%;
  height: auto;
  min-height: 0;

  :deep(.scrollbar-wrapper) {
    // 限制水平宽度
    overflow-x: hidden !important;
    background-color: transparent !important;

    .el-scrollbar__view {
      height: 100%;
      // 添加底部内边距，防止最后的菜单项被版本信息区域遮挡
      padding-bottom: 44px;
    }
  }
  // 滚动条
  :deep(.el-scrollbar__bar) {
    &.is-horizontal {
      // 隐藏水平滚动条
      display: none;
    }
  }
}

.el-menu {
  width: 100% !important;
  height: 100%;
  background: transparent !important;
  border: none;
}

:deep(.el-menu-item),
:deep(.el-sub-menu__title),
:deep(.el-sub-menu .el-menu-item) {
  // 图标、标题和展开箭头共用同一条 flex 中线。
  display: flex;
  align-items: center;
  height: var(--v3-sidebar-menu-item-height);
  line-height: var(--v3-sidebar-menu-item-height);
  background: transparent !important;

  &.is-active {
    background-color: var(--bussiness-sidebar-active-bg-color) !important;
  }

  &:hover {
    background-color: var(--bussiness-sidebar-active-bg-color) !important;
  }

  * {
    vertical-align: middle;
  }
}

:deep(.el-menu-item),
:deep(.el-sub-menu__title) {
  font-size: var(--v3-sidebar-menu-font-size) !important;
  color: var(--bussiness-sidebar-text-color) !important;
}

:deep(.el-menu-item.is-active),
:deep(.el-sub-menu.is-active > .el-sub-menu__title) {
  color: var(--bussiness-sidebar-active-text-color) !important;
}

:deep(.el-menu-item) {
  &.is-active {
    @include tip-line;
  }
}

.el-menu--collapse {
  :deep(.el-sub-menu) {
    &.is-active {
      .el-sub-menu__title {
        color: var(--bussiness-sidebar-active-text-color) !important;

        @include tip-line;
      }
    }
  }
}

// 版本信息区域样式
.version-info {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10; // 确保在滚动内容之上
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  color: var(--bussiness-sidebar-text-color);
  background-color: var(--bussiness-sidebar-background-color);
  transition: all 0.3s ease;

  &.collapsed {
    padding: 12px 8px;

    .version-number {
      display: none;
    }

    .version-history-icon {
      margin: 0 auto;
    }
  }

  .version-number {
    font-size: 14px;
    font-weight: 500;
    color: var(--bussiness-sidebar-text-color);
  }

  .version-history-icon {
    margin-left: 0.5em;
    font-size: 16px;
    color: var(--bussiness-sidebar-text-color);
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: var(--bussiness-sidebar-active-text-color);
    }
  }
}
</style>
